/**
 * React Router v7 Route for Ad Detail page
 *
 * This replaces the getInitialProps pattern with RR7's loader.
 * The loader receives {context, request, params} from entry.server.tsx (SSR)
 * or from entry.client.tsx (client-side navigation).
 *
 * Ad Detail is the page that shows vehicle details when users click on ads.
 * It handles multiple URL formats:
 * - Coches: /ford-kuga-...-59467554-covo.aspx
 * - Subscription: /renting/kia-picanto-...-59111348-vsre
 * - Motos: /ocasion/honda-cbr-...-12345678.htm
 */

import {redirect, useLoaderData} from 'react-router-dom'

import {getNavigationState} from '../../helpers/navigationStateCache.ts'
import {getInitialProps} from './getInitialProps.js'
import DetailPage from './index.js'

// Import styles (no tenant-specific styles for Detail)
import './styles/index.scss'

// Ad Detail regex patterns to extract adId from URL
const ADS_DETAIL_COCHES_REGEX = /\/.*-(?<adId>\d{4,9})-[a-z]{2,4}(vo|vn)\.aspx(\?.*)?$/
const ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX = /\/renting\/.*-(?<adId>\d{4,9})-vsre(\?.*)?$/
const ADS_DETAIL_MOTOS_REGEX = /\/(ocasion|nuevas)\/.*-(?<adId>\d{5,8})\.htm(\?.*)?$/
const ADS_DETAIL_PREVIEW_REGEX = /preview\/(?<adId>[a-zA-Z0-9]{1,})/

// Extract adId from URL pathname
function extractAdIdFromUrl(pathname: string): string | undefined {
  const patterns = [
    ADS_DETAIL_COCHES_REGEX,
    ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX,
    ADS_DETAIL_MOTOS_REGEX,
    ADS_DETAIL_PREVIEW_REGEX
  ]

  for (const pattern of patterns) {
    const match = pathname.match(pattern)
    if (match?.groups?.adId != null && match.groups.adId !== '') {
      return match.groups.adId
    }
  }

  return undefined
}

/**
 * Loader arguments interface
 */
interface LoaderArgs {
  context: any
  request: Request
  params: Record<string, string | undefined>
}

/**
 * Loader function for Ad Detail page - replaces getInitialProps
 *
 * @param args - Loader arguments with context, request, and params
 * @returns Props for Detail component
 */
export async function loader({context, request, params}: LoaderArgs): Promise<any> {
  // Extract route information from React Router v7 request
  const url = new URL(request.url)
  const pathname = url.pathname
  const search = url.search
  const query = Object.fromEntries(url.searchParams.entries())

  // Extract adId from URL using regex patterns
  const adId = extractAdIdFromUrl(pathname)

  // Add adId to params so getInitialProps can use it
  const enhancedParams = {
    ...params,
    adId
  }

  // Recovered from the navigation that brought the user here; RR7 keeps `state` out of
  // `LoaderFunctionArgs` (see helpers/navigationStateCache.ts).
  const state = getNavigationState(pathname)

  // Build routeInfo object compatible with getInitialProps
  const routeInfo = {
    location: {
      pathname,
      query,
      search,
      state
    },
    params: enhancedParams
  }

  // Call the existing getInitialProps function with the adapted parameters
  const data = await getInitialProps({
    routeInfo,
    context
  })

  // getInitialProps signals a redirect with the legacy @s-ui/react-initial-props
  // `__HTTP__` contract, which RR7 knows nothing about — it has to be turned into a real
  // `redirect()`, the way the other 35 routes that can return it already do (see
  // Make/route.tsx, and detailLegacy.route.tsx for this same page's other entry point).
  // Without this, a dead or expired ad answered 200 with an empty page (just the
  // advertising roadblock) instead of 302-ing to the listing, and the client then
  // re-ran this loader in a loop — ~900 gateway calls per pageview, measured. Legacy
  // (www.coches.net) 302s to /segunda-mano/ for the same URL; this restores that.
  // Reached through the `*` catch-all in routes.tsx, which is what real ad URLs match.
  const redirectTo: unknown = data?.__HTTP__?.redirectTo
  if (typeof redirectTo === 'string' && redirectTo !== '') {
    return redirect(redirectTo)
  }

  return data
}

/**
 * RR7 Component wrapper
 *
 * This wrapper uses useLoaderData() to get data from the loader
 * and passes it as props to the Detail component.
 */
export function Component() {
  const data = useLoaderData<any>()

  // DetailPage is the existing Detail component
  return <DetailPage {...data} />
}
